/**
 * Selectable tool catalog for managed agents.
 *
 * `ManagedAgent.tools` (see `managedAgents.ts`) stores opaque catalog ids so a
 * saved agent survives a tool disappearing from a future SDK release. This
 * file is the other half of that contract: the fixed, provider-scoped list of
 * ids the editor may offer and validate against right now.
 *
 * Two groups:
 * - `"builtin"` — tools native to the provider's own agent loop (Claude Code
 *   tools, Codex's `shell`/`apply_patch`/`web_search`/`update_plan`).
 * - `"taskin"` — Taskin's own MCP servers. Each server's individual tools are
 *   independently selectable (id `"<server>:<tool>"`, e.g.
 *   `"taskin_plane:plane_get_issue"`), enforced per-tool on both providers:
 *   Claude via `disallowedTools` (`mcp__<server>__<tool>`), Codex via an
 *   `agentGitMcp.ts` subprocess flag. A bare server id (e.g. `"taskin_plane"`)
 *   is kept only as the legacy "every tool of this server" form saved by
 *   agents created before per-tool selection existed — see
 *   `expandManagedAgentToolIds` below.
 *
 * Static and hand-maintained rather than generated, following
 * `MODEL_OPTIONS_BY_PROVIDER` in `model.ts`: this list changes on provider SDK
 * releases and Taskin MCP additions, both human-reviewed events, not runtime
 * data.
 */
import type { ProviderKind } from "./orchestration";

export type ManagedAgentToolGroup = "builtin" | "taskin";

export interface ManagedAgentToolOption {
  readonly id: string;
  readonly label: string;
  readonly group: ManagedAgentToolGroup;
  readonly providers: ReadonlyArray<ProviderKind>;
  /** Groups options into the editor's sections, e.g. "Plane", "Git", "GitHub". */
  readonly section: string;
  readonly sectionLabel: string;
  /** Set only for `group: "taskin"`: the real MCP server this tool lives in. */
  readonly mcpServerId?: (typeof MANAGED_AGENT_TASKIN_MCP_SERVER_IDS)[number];
  /** Set only for `group: "taskin"`: the real tool name inside that server. */
  readonly mcpToolName?: string;
  /**
   * Shown by the editor next to the option. Used for tools that cannot
   * actually be vetoed individually (Codex's `shell`/`apply_patch`), so the UI
   * can be honest about what selecting or clearing the checkbox does.
   */
  readonly note?: string;
}

/**
 * Every MCP server Taskin exposes to agents, keyed by the same name
 * `AGENT_MCP_TOOL_REGISTRIES` uses on the server. Kept as its own list (not
 * derived from `MANAGED_AGENT_TOOL_OPTIONS`) so `managedAgentToolCatalog.test.ts`
 * can assert on it directly without filtering the full catalog, and so the
 * server-side mirror test (`agentGitMcp.test.ts`) has one obvious array to
 * import and compare against `Object.keys(AGENT_MCP_TOOL_REGISTRIES)`.
 */
export const MANAGED_AGENT_TASKIN_MCP_SERVER_IDS = [
  "taskin_git",
  "taskin_github",
  "taskin_preview",
  "taskin_preview_control",
  "taskin_browser",
  "taskin_database",
  "taskin_agents",
  "taskin_thread_context",
  "taskin_plane",
  "taskin_plane_write",
  "taskin_plane_pages",
  "taskin_local",
  "taskin_memory",
  "taskin_qa",
] as const;

const BOTH_PROVIDERS: ReadonlyArray<ProviderKind> = ["claudeAgent", "codex"];

interface TaskinSubTool {
  readonly id: string;
  readonly label: string;
}

/**
 * The individual tool names inside each Taskin MCP server, mirroring
 * `AGENT_MCP_TOOL_REGISTRIES` in `apps/server/src/git/agentGitMcp.ts` — kept
 * hand-in-sync the same way `MANAGED_AGENT_TASKIN_MCP_SERVER_IDS` mirrors
 * `Object.keys(AGENT_MCP_TOOL_REGISTRIES)`. This is the source both the web
 * editor (one checkbox per entry) and the server-side enforcement helpers
 * (`readManagedAgentToolSelection` in `managedAgentSession.ts`) read to know
 * what a server's "every tool" expansion actually contains.
 */
export const MANAGED_AGENT_TASKIN_MCP_TOOLS_BY_SERVER: Readonly<
  Record<(typeof MANAGED_AGENT_TASKIN_MCP_SERVER_IDS)[number], ReadonlyArray<TaskinSubTool>>
> = {
  taskin_git: [
    { id: "git_commit", label: "Commit" },
    { id: "git_fetch", label: "Fetch" },
    { id: "git_pull", label: "Pull" },
    { id: "git_push", label: "Push" },
    { id: "git_create_pr", label: "Create pull request" },
    { id: "git_pr_fetch", label: "Fetch a pull request" },
    { id: "git_checkout", label: "Switch the environment's branch" },
    { id: "git_merge", label: "Merge a branch into the environment" },
    { id: "git_reset_to_base", label: "Reset the environment to its base" },
  ],
  taskin_github: [
    { id: "github_pr_view", label: "View pull request" },
    { id: "github_pr_comments", label: "Read PR review comments" },
    { id: "github_issue_view", label: "View issue" },
    { id: "github_issue_list", label: "List issues" },
    { id: "github_issue_search", label: "Search issues" },
  ],
  taskin_preview: [
    { id: "preview_status", label: "Preview status" },
    { id: "preview_logs", label: "Preview logs" },
  ],
  taskin_preview_control: [
    { id: "preview_start", label: "Start preview" },
    { id: "preview_browser_access", label: "Browser sign-in for preview" },
    { id: "preview_stop", label: "Stop preview" },
  ],
  taskin_browser: [{ id: "publish_screenshots", label: "Publish screenshots" }],
  taskin_database: [
    { id: "database_create", label: "Create database" },
    { id: "database_status", label: "Database status" },
    { id: "database_reset", label: "Reset database" },
    { id: "database_snapshot_create", label: "Snapshot database" },
    { id: "database_snapshot_list", label: "List snapshots" },
    { id: "database_snapshot_restore", label: "Restore snapshot" },
  ],
  taskin_agents: [
    { id: "agent_list", label: "List subagents" },
    { id: "agent_delegate_open", label: "Open delegation" },
    { id: "agent_delegate_send", label: "Send instruction" },
    { id: "agent_delegate_poll", label: "Poll delegation" },
    { id: "agent_delegate_close", label: "Close delegation" },
    { id: "delegate_read_file", label: "Read a delegate's file" },
    { id: "delegate_search", label: "Search a delegate's files" },
    { id: "delegate_diff", label: "Read a delegate's diff" },
  ],
  taskin_thread_context: [{ id: "read_thread_context", label: "Read thread context" }],
  taskin_plane: [
    { id: "plane_project_info", label: "Info del proyecto" },
    { id: "plane_plan_overview", label: "Ciclos y módulos" },
    { id: "plane_list_issues", label: "Listar issues" },
    { id: "plane_get_issue", label: "Leer un issue" },
    { id: "plane_my_issues", label: "Mis issues" },
    { id: "plane_list_members", label: "Miembros del workspace" },
    { id: "plane_diagnostics", label: "Diagnóstico" },
  ],
  taskin_plane_write: [
    { id: "plane_add_comment", label: "Comentar" },
    { id: "plane_set_state", label: "Cambiar estado" },
    { id: "plane_assign_self", label: "Autoasignarse" },
    { id: "plane_create_issue", label: "Crear issue" },
    { id: "plane_update_issue", label: "Modificar issue" },
    { id: "plane_set_cycle", label: "Mover a un ciclo" },
    { id: "plane_set_modules", label: "Asignar módulos" },
    { id: "plane_create_cycle", label: "Crear ciclo" },
    { id: "plane_create_module", label: "Crear módulo" },
  ],
  // Pages run on a bot user's session, not the API token, so this server can be
  // unavailable while the two above work. There is no "edit page": writing HTML
  // to an existing page corrupts what the read tool returns.
  taskin_plane_pages: [
    { id: "plane_page_find", label: "Buscar páginas" },
    { id: "plane_page_read", label: "Leer una página" },
    { id: "plane_page_create", label: "Crear página" },
    { id: "plane_page_rename", label: "Renombrar página" },
    { id: "plane_page_archive", label: "Archivar página" },
  ],
  // Runs on the user's own machine through the desktop app, so this server is
  // unavailable whenever nobody has one connected — and every call is gated by
  // `p.local.exec` on top of whatever the agent has ticked here.
  taskin_local: [
    { id: "local_exec_list", label: "Listar comandos locales" },
    { id: "local_exec_run", label: "Ejecutar un comando local" },
    { id: "local_exec_stop", label: "Detener un proceso local" },
  ],
  // What this installation already worked out, and how an agent adds to it.
  // Reading and proposing are separate ticks on purpose: an agent that should
  // follow the house procedures is not necessarily one whose proposals anyone
  // wants to triage.
  taskin_memory: [
    { id: "memory_read", label: "Leer una memoria" },
    { id: "memory_propose", label: "Proponer una memoria" },
  ],
  // Operating a project's shared environment (QA, staging) from a conversation.
  // Each tool is ALSO gated on a permission of the thread's human owner —
  // `p.env.use` to look, `p.env.sync` to change, `p.env.manage` to create,
  // `p.pr.preview` to try a pull request — so ticking one here grants nothing on
  // its own. The three an agent needs to be considered capable of running a sync
  // are `qa_env_preflight`, `qa_env_sync` and `qa_env_config_set`; trying a pull
  // request also needs `qa_try_pr` and `qa_back_to_base` (see
  // `managedAgentQaCapability.ts`).
  taskin_qa: [
    { id: "qa_env_list", label: "Listar entornos" },
    { id: "qa_env_status", label: "Estado de un entorno" },
    { id: "qa_env_preflight", label: "Preflight de la rama base" },
    { id: "qa_env_config_list", label: "Ver claves de configuración" },
    { id: "qa_env_config_set", label: "Escribir una clave" },
    { id: "qa_env_run_setup", label: "Repetir el setup" },
    { id: "qa_env_sql", label: "Ejecutar SQL" },
    { id: "qa_env_create", label: "Crear un entorno" },
    { id: "qa_env_copy_data", label: "Copiar datos entre entornos" },
    { id: "qa_env_sync", label: "Sincronizar con la rama base" },
    { id: "qa_env_reset_data", label: "Restaurar datos" },
    { id: "qa_env_snapshots", label: "Copias de la base de datos" },
    { id: "qa_try_pr", label: "Probar un PR sobre el entorno" },
    { id: "qa_back_to_base", label: "Volver a la rama base" },
    { id: "qa_preview_list", label: "Listar previews de PR" },
    { id: "qa_preview_create", label: "Crear una preview aislada de un PR" },
    { id: "qa_preview_refresh", label: "Reconstruir una preview" },
    { id: "qa_preview_extend", label: "Alargar la caducidad de una preview" },
    { id: "qa_preview_destroy", label: "Destruir una preview" },
  ],
};

/** UI section id + label each Taskin MCP server's tools are grouped under. */
const TASKIN_SECTION_BY_SERVER: Record<
  (typeof MANAGED_AGENT_TASKIN_MCP_SERVER_IDS)[number],
  { readonly section: string; readonly sectionLabel: string }
> = {
  taskin_git: { section: "git", sectionLabel: "Git" },
  taskin_github: { section: "github", sectionLabel: "GitHub" },
  taskin_preview: { section: "preview", sectionLabel: "Preview" },
  taskin_preview_control: { section: "preview", sectionLabel: "Preview" },
  taskin_browser: { section: "browser", sectionLabel: "Browser" },
  taskin_database: { section: "database", sectionLabel: "Database" },
  taskin_agents: { section: "agents", sectionLabel: "Delegation" },
  taskin_thread_context: { section: "thread_context", sectionLabel: "Thread context" },
  taskin_plane: { section: "plane", sectionLabel: "Plane" },
  taskin_plane_write: { section: "plane", sectionLabel: "Plane" },
  taskin_plane_pages: { section: "plane", sectionLabel: "Plane" },
  taskin_local: { section: "local_exec", sectionLabel: "Ejecución local" },
  taskin_memory: { section: "memory", sectionLabel: "Memoria" },
  taskin_qa: { section: "qa", sectionLabel: "Entornos (QA)" },
};

export const MANAGED_AGENT_TOOL_OPTIONS: ReadonlyArray<ManagedAgentToolOption> = [
  // ---- Claude Code builtin tools ----------------------------------------
  {
    id: "Bash",
    label: "Bash",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "Read",
    label: "Read",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "Write",
    label: "Write",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "Edit",
    label: "Edit",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "Glob",
    label: "Glob",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "Grep",
    label: "Grep",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "WebSearch",
    label: "Web Search",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "WebFetch",
    label: "Web Fetch",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "Task",
    label: "Task (subagent delegation)",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "TodoWrite",
    label: "Todo Write",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },
  {
    id: "NotebookEdit",
    label: "Notebook Edit",
    group: "builtin",
    providers: ["claudeAgent"],
    section: "claude_builtin",
    sectionLabel: "Claude Code tools",
  },

  // ---- Codex builtin tools -----------------------------------------------
  {
    id: "web_search",
    label: "Web Search",
    group: "builtin",
    providers: ["codex"],
    section: "codex_builtin",
    sectionLabel: "Codex tools",
  },
  {
    id: "update_plan",
    label: "Update Plan",
    group: "builtin",
    providers: ["codex"],
    section: "codex_builtin",
    sectionLabel: "Codex tools",
  },
  {
    id: "shell",
    label: "Shell",
    group: "builtin",
    providers: ["codex"],
    section: "codex_builtin",
    sectionLabel: "Codex tools",
    note: "Codex cannot veto shell individually — only sandbox and approval mode limit it.",
  },
  {
    id: "apply_patch",
    label: "Apply Patch",
    group: "builtin",
    providers: ["codex"],
    section: "codex_builtin",
    sectionLabel: "Codex tools",
    note: "Codex cannot veto apply_patch individually — only sandbox and approval mode limit it.",
  },

  // ---- Taskin MCP servers, one option per individual tool ---------------
  ...MANAGED_AGENT_TASKIN_MCP_SERVER_IDS.flatMap(
    (serverId): ReadonlyArray<ManagedAgentToolOption> => {
      const { section, sectionLabel } = TASKIN_SECTION_BY_SERVER[serverId];
      return MANAGED_AGENT_TASKIN_MCP_TOOLS_BY_SERVER[serverId].map(
        (tool): ManagedAgentToolOption => ({
          id: `${serverId}:${tool.id}`,
          label: tool.label,
          group: "taskin",
          providers: BOTH_PROVIDERS,
          section,
          sectionLabel,
          mcpServerId: serverId,
          mcpToolName: tool.id,
        }),
      );
    },
  ),
];

/** The options a given provider's agent may actually select from. */
export const getManagedAgentToolOptions = (
  provider: ProviderKind,
): ReadonlyArray<ManagedAgentToolOption> =>
  MANAGED_AGENT_TOOL_OPTIONS.filter((option) => option.providers.includes(provider));

/**
 * True if `toolId` is a legacy whole-server grant (e.g. `"taskin_plane"`)
 * rather than a granular `"<server>:<tool>"` id or a builtin tool id. Agents
 * saved before per-tool selection existed store server ids bare.
 */
export const isLegacyTaskinServerToolId = (
  toolId: string,
): toolId is (typeof MANAGED_AGENT_TASKIN_MCP_SERVER_IDS)[number] =>
  (MANAGED_AGENT_TASKIN_MCP_SERVER_IDS as ReadonlyArray<string>).includes(toolId);

/**
 * Expand a saved `ManagedAgent.tools` list into fully granular ids: a legacy
 * whole-server id becomes every one of that server's individual tool ids — it
 * meant "all of them" when it was saved, before per-tool selection existed.
 * Builtin ids and already-granular `"<server>:<tool>"` ids pass through
 * unchanged. Both the editor (to pre-check boxes for an old agent) and the
 * server-side enforcement helpers read this so a pre-existing agent keeps
 * exactly the access it had before this feature shipped.
 */
export const expandManagedAgentToolIds = (toolIds: ReadonlyArray<string>): ReadonlyArray<string> =>
  toolIds.flatMap(
    (id): ReadonlyArray<string> =>
      isLegacyTaskinServerToolId(id)
        ? MANAGED_AGENT_TASKIN_MCP_TOOLS_BY_SERVER[id].map((tool) => `${id}:${tool.id}`)
        : [id],
  );
